import React from 'react';
import { View } from '@shared/components/View';
import { MBAutosizeTextInput } from '@shared/components/textInput';
import { MBChatInputUtil } from '@shared/scenes/chat/input/util/index';
import { MBAnalytics } from '@shared/util/analytics';
import { MBChatInputBar } from '../containers/bar';
import { MBChatInputGIFModal } from '../containers/gifModal';
import { MBUrgentBanner } from '../containers/urgentBanner';
import { MBChatInputBarProps } from './bar';
import { MBChatInputMentionListProps } from './mention/list';
import { MBChatInputMentionListWithAnimation } from './mention/list/withAnimation';
import { Styles } from './styles';

export interface MBChatInputProps {
  // The identifier by which we identify the content
  // of this specific chat input in the store
  draftId: string;
  initialValue?: string;
  autoFocus?: boolean;
  isEditing?: boolean;
  placeholder?: MBChatInputBarProps['placeholder'];
  hideUrgent?: MBChatInputBarProps['hideUrgent'];
  users: MBChatInputMentionListProps['users'];
  groups: MBChatInputMentionListProps['groups'];
  onSend: (message: string) => void;
  onCancelEdit: () => void;
  onBlur?: () => void;
  onFocus?: () => void;
  onUnmount?(message: string): void;
  onChangeText?: (message: string) => void;
  paddingHorizontal?: number;
}

interface MBChatInputState {
  value: string;
  cursorPosition: number;
  users: MBChatInputMentionListProps['users'];
  groups: MBChatInputMentionListProps['groups'];
  numUrgentRecipients: number;
}

export class MBChatInput extends React.PureComponent<MBChatInputProps, MBChatInputState> {
  state: MBChatInputState = {
    value: this.props.initialValue || '',
    cursorPosition: !!this.props.initialValue ? this.props.initialValue.length : 0,
    users: [],
    groups: [],
    numUrgentRecipients: 0,
  };
  inputBar = React.createRef<MBAutosizeTextInput>();

  /*
        When the text value or the cursor position changes, we update
        what mentions are visible. Only users and groups (eg @all)
        are visible if their name/username matches the current
        mention query.
    */
  updateMentions() {
    const query = MBChatInputUtil.mentionQuery(this.state.value, this.state.cursorPosition);
    if (query === undefined) {
      this.setState({ users: [], groups: [] });
      return;
    }

    if (query === '') {
      this.setState({ users: this.props.users, groups: this.props.groups });
      return;
    }

    const users = this.props.users.filter(u => u.username.includes(query));
    const groups = this.props.groups.filter(g => g.id.includes(query));
    this.setState({ users, groups });
  }

  updateNumUrgentRecipients() {
    const numUrgentRecipients = MBChatInputUtil.numUrgentRecipients(
      this.state.value,
      this.props.users,
    );
    this.setState({ numUrgentRecipients });
  }

  componentDidUpdate(prevProps: MBChatInputProps, prevState: MBChatInputState) {
    if (
      prevState.value !== this.state.value ||
      prevState.cursorPosition !== this.state.cursorPosition ||
      prevProps.users !== this.props.users ||
      prevProps.groups !== this.props.groups
    ) {
      this.updateMentions();
    }

    if (
      prevState.value !== this.state.value ||
      prevProps.users !== this.props.users ||
      prevProps.groups !== this.props.groups
    ) {
      // Note(fant): we only recalculate this when we have to.
      // We don't want to do this every time in render
      this.updateNumUrgentRecipients();
    }
  }

  componentWillUnmount() {
    if (!!this.props.onUnmount) {
      this.props.onUnmount(this.state.value);
    }
  }

  /*
        When we click on a mention, we replace the
        current mention word with the full mention.
        TODO(fant): add unit tests for this logic.
    */
  onAddMention = (mention: string) => {
    const { string, cursorPosition } = MBChatInputUtil.addMention(
      this.state.value,
      this.state.cursorPosition,
      mention,
    );
    this.setState({ cursorPosition, value: string });
    if (!!this.props.onChangeText) {
      this.props.onChangeText(string);
    }
  };

  onPressUser = (user: MBAPI.ShortUser) => {
    MBAnalytics.track('Chat::Input::Mention::PressUser', {
      id: user.id,
      draftId: this.props.draftId,
    });
    this.onAddMention(user.username);
    this.inputBar.current?.focus();
  };

  onPressGroup = (group: MBAPI.ChannelGroup) => {
    MBAnalytics.track('Chat::Input::Mention::PressGroup', {
      id: group.id,
      draftId: this.props.draftId,
    });
    this.onAddMention(group.id);
    this.inputBar.current?.focus();
  };

  onChangeText = (value: string) => {
    this.setState({ value });
    if (!!this.props.onChangeText) {
      this.props.onChangeText(value);
    }
  };

  onAddEmoji = (emoji: string) => {
    const before = this.state.value.substr(0, this.state.cursorPosition);
    const after = this.state.value.substr(this.state.cursorPosition);
    const value = [before, emoji, after].join('');
    this.onChangeText(value);
    this.setState({ cursorPosition: this.state.cursorPosition + emoji.length });
  };

  onCursorPositionChange = (cursorPosition: number) => this.setState({ cursorPosition });
  onSend = () => {
    this.props.onSend(this.state.value);
    this.setState({ value: '', cursorPosition: 0 });
    if (!!this.props.onChangeText) {
      this.props.onChangeText('');
    }
  };

  onCancelEdit = () => {
    this.setState({ value: '' });
    this.props.onCancelEdit();
  };

  render() {
    const { users, groups, numUrgentRecipients } = this.state;
    const { paddingHorizontal = 2 } = this.props;
    return (
      <View relative paddingHorizontal={paddingHorizontal}>
        <MBChatInputMentionListWithAnimation
          style={Styles.mentionListPosition}
          users={users}
          groups={groups}
          onPressUser={this.onPressUser}
          onPressGroup={this.onPressGroup}
        />

        <MBChatInputBar
          innerRef={this.inputBar}
          draftId={this.props.draftId}
          autoFocus={this.props.autoFocus}
          isEditing={this.props.isEditing ?? false}
          hideUrgent={this.props.hideUrgent}
          value={this.state.value}
          placeholder={this.props.placeholder}
          onSend={this.onSend}
          onCancelEdit={this.onCancelEdit}
          onBlur={this.props.onBlur}
          onFocus={this.props.onFocus}
          onChangeText={this.onChangeText}
          onSubmitEditing={this.onSend}
          onCursorPositionChange={this.onCursorPositionChange}
          onAddEmoji={this.onAddEmoji}
        />

        {numUrgentRecipients > 0 && (
          <MBUrgentBanner draftId={this.props.draftId} count={numUrgentRecipients} />
        )}
        <MBChatInputGIFModal draftId={this.props.draftId} />
      </View>
    );
  }
}
